// The client's wording (26 Oct). Never "certified", and never a negative label.
export const FCA_LABEL = 'FCA-verified';

export const FCA_CONTACT_EMAIL = 'editors@newspage.media';

// The client's directory rule (27 Oct), shown to members and in the admin queue so both apply the same one.
export const FCA_DIRECTORY_RULE =
    'If your firm has an FRN, we require it for you to appear in the Newspage directory. Only practising experts who advise clients themselves will be listed in the directory.';

export const FRN_MAX = 7;
export const ROLE_MAX = 160;
export const REGISTER_NAME_MAX = 255;
export const NOTE_MAX = 500;

const FRN_RE = /^\d{6,7}$/;

export const FIRM_KINDS = [
    { value: 'authorised', label: 'Directly authorised firm' },
    { value: 'appointed_representative', label: 'Appointed representative' },
    { value: 'other', label: 'Other' },
];

export const firmKindLabel = (kind) =>
    FIRM_KINDS.find(({ value }) => value === kind)?.label || kind;

export const approvedByLine = ({ approvedVia, decidedBy } = {}) => {
    if (approvedVia === 'register')
        return 'approved automatically by the FCA Register check';
    if (approvedVia === 'admin')
        return `approved by ${decidedBy?.name || 'an admin'}`;
    return '';
};

export const STATUS_LABELS = {
    none: 'No check',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    lapsed: 'Lapsed',
};

const text = (value) => (typeof value === 'string' ? value.trim() : '');

export const normaliseFrn = (value) => text(value).replace(/\s+/g, '');

// The firm page itself is keyed by the Register's internal id, so a search by FRN is the link we can build.
export const registerUrl = (frn) =>
    `https://register.fca.org.uk/s/search?q=${encodeURIComponent(
        normaliseFrn(String(frn ?? ''))
    )}&type=Companies`;

const londonDate = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
});

// Fixed zone so the cached profile HTML and the browser agree on the day.
export const fcaDate = (value) => {
    const date = value ? new Date(value) : null;
    return !date || Number.isNaN(date.valueOf()) ? '' : londonDate.format(date);
};

// The API's rule (labelShows): a check shows until its expiresAt, which the API sets only while the
// Register API key renews checks; without one it is null and nothing expires. The public label and the
// member's own status both use this, so they never disagree.
export const hasFca = (fca, now = Date.now()) => {
    const checked = fca?.checkedAt ? new Date(fca.checkedAt).getTime() : NaN;
    if (Number.isNaN(checked)) return false;
    const expires = fca.expiresAt ? new Date(fca.expiresAt).getTime() : NaN;
    return Number.isNaN(expires) || now <= expires;
};

export const checkedLine = (fca) => {
    const date = fcaDate(fca?.checkedAt);
    return date
        ? `FCA reference checked by Newspage on ${date}`
        : 'FCA reference checked by Newspage';
};

// The client's call (2 Nov): the evidence under the public label, as page text. Same gate as the label.
export const fcaEvidence = (fca, now = Date.now()) => {
    const frn = normaliseFrn(fca?.frn);
    if (!hasFca(fca, now) || !frn) return null;
    const date = fcaDate(fca.checkedAt);
    const principal = text(fca.principalName);
    return {
        frn,
        href: registerUrl(frn),
        checked: date ? `checked by Newspage ${date}` : 'checked by Newspage',
        principal: principal ? `Appointed representative of ${principal}` : '',
    };
};

// Only while the API sends `fca.frn`: for a current, unexpired check (PUBLISH_FRN_IN_SCHEMA). The JSON-LD is
// built with the page, so a cached page keeps it until rebuilt: fcaRevalidate times that rebuild to expiresAt.
export const fcaIdentifier = (fca) =>
    hasFca(fca) && normaliseFrn(fca.frn)
        ? {
              '@type': 'PropertyValue',
              propertyID: 'FCA FRN',
              value: normaliseFrn(fca.frn),
          }
        : undefined;

export const REJECTED_LINE =
    "We couldn't confirm this, and saving the same FRN again won't change that — contact us if you think the Register matches.";

export const memberStatusLine = (details = {}, now = Date.now()) => {
    const { status, checkedAt } = details;
    if (status === 'pending')
        return 'Pending: Newspage is checking it against the FCA Register.';
    if (status === 'approved' && !hasFca(details, now))
        return `Your check is out of date, so the ${FCA_LABEL} label is off until Newspage checks it again.`;
    if (status === 'approved') {
        const date = fcaDate(checkedAt);
        return `Checked${
            date ? ` on ${date}` : ''
        }: your profile shows ${FCA_LABEL}.`;
    }
    if (status === 'rejected') return REJECTED_LINE;
    return '';
};

// Mirrors PUT /profiles/:id/fca. The FRN may stay empty when only the practising question is answered;
// a saved FRN is taken off with Remove, not by emptying the field.
export const validateFcaDetails = (
    { frn, practisingAdviser, practisingRole },
    savedFrn = ''
) => {
    const errors = {};
    const cleanFrn = normaliseFrn(frn);
    const role = text(practisingRole);
    const answered = practisingAdviser === true || practisingAdviser === false;

    if (!cleanFrn && normaliseFrn(savedFrn))
        errors.frn = 'Enter your FRN, or use Remove FCA details.';
    else if (!cleanFrn && !answered)
        errors.frn = 'Enter your FCA reference number (FRN).';
    else if (cleanFrn && !FRN_RE.test(cleanFrn))
        errors.frn = 'An FRN is 6 or 7 digits, for example 123456.';

    if (practisingAdviser === false && !role)
        errors.practisingRole = 'Tell us what you do instead.';
    else if (role.length > ROLE_MAX)
        errors.practisingRole = `Keep it to ${ROLE_MAX} characters.`;

    if (Object.keys(errors).length) return { errors, payload: null };

    const payload = cleanFrn ? { frn: cleanFrn } : {};
    if (answered) {
        payload.practisingAdviser = practisingAdviser;
        payload.practisingRole = practisingAdviser ? null : role;
    }
    return { errors, payload };
};

export const toFcaForm = (details) => ({
    frn: details?.frn ?? '',
    practisingAdviser:
        details?.practisingAdviser == null
            ? null
            : Boolean(details.practisingAdviser),
    practisingRole: details?.practisingRole ?? '',
});

export const statedChanged = (form, saved) =>
    normaliseFrn(form.frn) !== normaliseFrn(saved?.frn ?? '');

export const fcaFormDirty = (form, saved) => {
    const before = toFcaForm(saved);
    return (
        statedChanged(form, saved) ||
        form.practisingAdviser !== before.practisingAdviser ||
        (form.practisingAdviser === false &&
            text(form.practisingRole) !== text(before.practisingRole))
    );
};

// Mirrors POST /profiles/:id/fca/approve.
export const validateApproval = ({
    registerName,
    firmKind,
    principalName,
    note,
}) => {
    const errors = {};
    const name = text(registerName);
    const principal = text(principalName);
    const cleanNote = text(note);
    const ar = firmKind === 'appointed_representative';

    if (!name) errors.registerName = 'Enter the name the Register shows.';
    else if (name.length > REGISTER_NAME_MAX)
        errors.registerName = `Keep it to ${REGISTER_NAME_MAX} characters.`;

    if (!FIRM_KINDS.some(({ value }) => value === firmKind))
        errors.firmKind = 'Choose the firm kind.';

    if (principal.length > REGISTER_NAME_MAX)
        errors.principalName = `Keep it to ${REGISTER_NAME_MAX} characters.`;

    if (cleanNote.length > NOTE_MAX)
        errors.note = `Keep it to ${NOTE_MAX} characters.`;

    if (Object.keys(errors).length) return { errors, payload: null };

    const payload = { registerName: name, firmKind };
    if (ar && principal) payload.principalName = principal;
    if (cleanNote) payload.note = cleanNote;
    return { errors, payload };
};

// The API refuses a decision on a claim that changed since the admin looked at it (409). Without a
// claim id, approving is an override for whatever FRN the profile states now.
export const decisionTarget = (checkId) =>
    checkId ? { checkId } : { override: true };

// Mirrors POST /profiles/:id/fca/reject.
export const validateRejection = ({ note }) => {
    const cleanNote = text(note);
    if (!cleanNote)
        return {
            error: 'Say why, so the next admin knows.',
            payload: null,
        };
    if (cleanNote.length > NOTE_MAX)
        return { error: `Keep it to ${NOTE_MAX} characters.`, payload: null };
    return { error: null, payload: { note: cleanNote } };
};

// ISR seconds for a profile page: a current check that expires sooner than the usual day rebuilds the page
// by then (never sooner than a minute), so the label and the FRN identifier don't outlive it.
export const fcaRevalidate = (fca, now = Date.now(), fallback = 86400) => {
    if (!hasFca(fca, now) || !fca.expiresAt) return fallback;
    const seconds = Math.ceil((new Date(fca.expiresAt).getTime() - now) / 1000);
    return Math.min(fallback, Math.max(60, seconds));
};
